import { useState, type FormEvent } from 'react';
import { loginUser } from '../api/auth';
import HexLattice from '../components/HexLattice';
import { EyeIcon, EyeOffIcon, HexMark } from '../components/icons';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { redirect_url } = await loginUser(email, password);
      window.location.href = redirect_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-hx-bg font-sans text-hx-text lg:flex-row">
      {/* Hero — visible desde lg */}
      <div className="relative hidden overflow-hidden border-r border-hx-line px-12 py-12 lg:flex lg:w-[46%] lg:flex-col lg:justify-between">
        <HexLattice className="absolute inset-0 h-full w-full [mask-image:radial-gradient(ellipse_75%_75%_at_28%_38%,black_5%,transparent_72%)]" />

        <div className="relative z-10 flex items-center gap-2 text-hx-muted">
          <HexMark className="h-4 w-4 text-hx-amber" />
          <span className="font-mono text-xs uppercase tracking-[0.2em]">Plataforma interna</span>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <HexMark className="h-9 w-9 text-hx-amber" />
            <h1 className="font-display text-5xl font-semibold tracking-tight">Hexa</h1>
          </div>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-hx-muted">
            Un acceso para todos los sistemas del equipo.
          </p>
        </div>

        <p className="relative z-10 font-mono text-[11px] tracking-widest text-hx-muted-2">
          ACCESO RESTRINGIDO · V0.1
        </p>
      </div>

      {/* Franja hero — visible en mobile/tablet */}
      <div className="relative flex h-32 items-center justify-center overflow-hidden border-b border-hx-line lg:hidden">
        <HexLattice className="absolute inset-0 h-full w-full [mask-image:radial-gradient(ellipse_85%_100%_at_50%_50%,black_20%,transparent_80%)]" />
        <div className="relative z-10 flex items-center gap-2.5">
          <HexMark className="h-7 w-7 text-hx-amber" />
          <span className="font-display text-2xl font-semibold">Hexa</span>
        </div>
      </div>

      {/* Formulario */}
      <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10">
        <div className="hx-rise w-full max-w-sm">
          <h2 className="font-display text-2xl font-semibold">Iniciar sesión</h2>
          <p className="mt-1.5 text-sm text-hx-muted">
            Ingresa tus credenciales para acceder a la plataforma.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-hx-muted"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nombre@hexa.dev"
                className="w-full rounded-md border border-hx-line bg-hx-panel-2 px-3.5 py-2.5 text-sm text-hx-text placeholder-hx-muted-2 outline-none transition focus:border-hx-amber focus:ring-2 focus:ring-hx-amber/25"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-hx-muted"
              >
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-md border border-hx-line bg-hx-panel-2 px-3.5 py-2.5 pr-10 text-sm text-hx-text placeholder-hx-muted-2 outline-none transition focus:border-hx-amber focus:ring-2 focus:ring-hx-amber/25"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-hx-muted transition hover:text-hx-amber-soft focus-visible:text-hx-amber-soft focus-visible:outline-none"
                >
                  {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p
                role="alert"
                className="rounded-md border border-hx-error/30 bg-hx-error-bg px-3.5 py-2.5 text-sm text-hx-error"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                clipPath:
                  'polygon(14px 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0 50%)',
              }}
              className="flex w-full items-center justify-center gap-2 bg-hx-amber py-3 text-sm font-semibold text-[#1a1206] transition hover:bg-hx-amber-soft disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading && (
                <span className="hx-spin h-3.5 w-3.5 rounded-full border-2 border-[#1a1206]/30 border-t-[#1a1206]" />
              )}
              {loading ? 'Iniciando sesión…' : 'Iniciar sesión'}
            </button>
          </form>

          <p className="mt-8 font-mono text-[11px] text-hx-muted-2">
            Tu sesión se gestiona de forma segura.
          </p>
        </div>
      </div>
    </div>
  );
}
